import { useEffect, useRef, useState } from 'react'
import { formatVersion } from '../domain/timestamp'
import type { Report } from '../domain/types'
import { Button } from './Button'
import { focusMain } from './focus'

const VISIBLE_MS = 10_000

/**
 * Says what a removal took out, and offers it back.
 *
 * Removal asks for no confirmation, which keeps tidying a session quick; this
 * is what makes that safe. It also explains an otherwise abrupt return to the
 * start page when the last assessment goes.
 *
 * The live region is always present, so the message is announced when it
 * appears; focus moves to Undo, which the button that was pressed may no
 * longer exist to keep; and the timer waits while the notice is being used.
 * Being used means pointed at, or reached or pressed from the keyboard: not
 * the focus placed on Undo here, or the notice would never go.
 */
export function UndoNotice({
  removed,
  onUndo,
  onDone,
}: {
  removed: Report[]
  onUndo: () => void
  onDone: () => void
}) {
  const undo = useRef<HTMLButtonElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const placing = useRef(false)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (removed.length === 0) return
    placing.current = true
    undo.current?.focus()
    placing.current = false
  }, [removed])

  useEffect(() => {
    if (removed.length === 0 || paused) return
    const timer = setTimeout(() => {
      // The buttons go with the notice; focus must not go with them.
      if (box.current?.contains(document.activeElement)) focusMain()
      onDone()
    }, VISIBLE_MS)
    return () => clearTimeout(timer)
  }, [removed, paused, onDone])

  const leave = (action: () => void) => () => {
    focusMain()
    setPaused(false)
    action()
  }

  const [first] = removed
  return (
    // Pausing the countdown while pointer or focus is on it (WCAG 2.2.1) is not
    // an interaction of its own; the buttons inside remain the controls.
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={box}
      className={removed.length > 0 ? 'undo-notice' : 'visually-hidden'}
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => {
        if (!placing.current) setPaused(true)
      }}
      onKeyDown={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {first && (
        <>
          <span>
            Removed {first.software.name}
            {first.software.version ? ` ${formatVersion(first.software.version)}` : ''}
            {removed.length > 1 ? ` and ${removed.length - 1} more` : ''}.
          </span>
          <Button
            ref={undo}
            size="small"
            tone="quiet"
            icon="refresh"
            label="Undo"
            onClick={leave(onUndo)}
          />
          <Button
            size="small"
            tone="quiet"
            icon="close"
            iconOnly
            label="Dismiss"
            onClick={leave(onDone)}
          />
        </>
      )}
    </div>
  )
}
